/**
 * SeniorModeContext — browser-level Senior Mode toggle.
 *
 * Works BEFORE login (localStorage) and merges with the server-persisted
 * `user.senior_mode` after login. Applies a global `.senior-mode` class on
 * <html> which is picked up by CSS (index.css) to boost base font size,
 * increase tap targets, and simplify contrast.
 *
 * Priority (highest → lowest):
 *   1. logged-in user's `senior_mode` field (server-persisted)
 *   2. localStorage(vt_senior_mode) — browser preference
 *   3. false (default)
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const KEY = "vt_senior_mode";
const SeniorModeContext = createContext(null);

export function SeniorModeProvider({ children }) {
    const { user, refresh } = useAuth();
    const [browserOn, setBrowserOn] = useState(() => {
        try { return localStorage.getItem(KEY) === "true"; } catch { return false; }
    });
    // Optimistic override so the switch flips INSTANTLY for logged-in users
    // while the server round-trip (/privacy/preferences + refresh) completes.
    const [override, setOverride] = useState(null);

    // Clear the optimistic override once the server-persisted value arrives.
    useEffect(() => { setOverride(null); }, [user?.senior_mode]);

    // If the user is logged in and their profile has senior_mode, prefer that.
    const enabled = useMemo(() => {
        if (override !== null) return override;
        if (user && typeof user === "object" && typeof user.senior_mode === "boolean") {
            return user.senior_mode;
        }
        return browserOn;
    }, [override, user, browserOn]);

    // Apply/remove the root class whenever the effective state changes.
    useEffect(() => {
        try { document.documentElement.classList.toggle("senior-mode", enabled); } catch { /* SSR safety */ }
        return () => {
            try { document.documentElement.classList.remove("senior-mode"); } catch { /* SSR safety */ }
        };
    }, [enabled]);

    // Cross-tab sync
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === KEY) setBrowserOn(e.newValue === "true");
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const toggle = useCallback(() => {
        const next = !enabled;
        setOverride(next);
        setBrowserOn(next);
        try { localStorage.setItem(KEY, String(next)); } catch { /* quota */ }
        // Persist whenever a session cookie exists (httpOnly — not readable
        // here, so POST unconditionally; a guest just gets a harmless 401 and
        // keeps the browser-level toggle).
        api.post("/privacy/preferences", { senior_mode: next })
            .then(() => { if (typeof refresh === "function") refresh(); })
            .catch(() => { /* guest / offline — browser toggle still applies */ });
    }, [enabled, refresh]);

    const value = useMemo(() => ({ enabled, toggle }), [enabled, toggle]);
    return <SeniorModeContext.Provider value={value}>{children}</SeniorModeContext.Provider>;
}

export function useSeniorMode() {
    const ctx = useContext(SeniorModeContext);
    if (!ctx) throw new Error("useSeniorMode must be used within SeniorModeProvider");
    return ctx;
}
